import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Play,
  Pause,
  Clock,
  Tag,
  User,
  ShoppingCart,
  Check,
  Loader2,
  ArrowLeft,
  MessageSquare,
  FileText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const TrackDetailPage = () => {
  const { trackId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { currentTrack, isPlaying, playTrack } = useAudioPlayer();
  
  const [track, setTrack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState('sync');
  const [userLicenses, setUserLicenses] = useState([]);

  const isCurrentTrack = currentTrack?.id === track?.id;
  const isTrackPlaying = isCurrentTrack && isPlaying;

  const licenses = [
    {
      type: 'sync',
      name: 'Sync License',
      description: 'Use in videos, films, and social media content',
      features: ['YouTube/TikTok/Instagram', 'Up to 100K views', 'Non-exclusive'],
      priceKey: 'price_sync'
    },
    {
      type: 'commercial',
      name: 'Commercial License',
      description: 'Full commercial usage rights for business',
      features: ['Unlimited views', 'Advertising & marketing', 'Broadcast rights'],
      priceKey: 'price_commercial'
    },
    {
      type: 'nft',
      name: 'NFT/Token License',
      description: 'Full ownership rights with exclusive perks',
      features: ['Exclusive ownership', 'Resale rights', 'Royalty sharing'],
      priceKey: 'price_nft'
    },
    {
      type: 'lyrics',
      name: 'Lyrics License',
      description: 'Purchase and download the original lyrics file',
      features: ['PDF or TXT download', 'Personal & study use', 'Songwriter credit'],
      priceKey: 'price_lyrics'
    }
  ];

  useEffect(() => {
    const fetchTrack = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/tracks/${trackId}`);
        setTrack(response.data);
      } catch (error) {
        console.error('Error fetching track:', error);
        toast.error('Track not found');
        navigate('/marketplace');
      } finally {
        setLoading(false);
      }
    };

    fetchTrack();
  }, [trackId, navigate]);

  useEffect(() => {
    const fetchUserLicenses = async () => {
      if (authLoading) return;
      if (!isAuthenticated) return;
      try {
        const response = await axios.get(`${BACKEND_URL}/api/purchases`);
        const trackLicenses = response.data
          .filter(p => p.track_id === trackId && p.status === 'completed')
          .map(p => p.license_type);
        setUserLicenses(trackLicenses);
      } catch (error) {
        console.error('Error fetching purchases:', error);
      }
    };

    fetchUserLicenses();
  }, [authLoading, isAuthenticated, trackId]);

  const handlePlay = () => {
    if (track) {
      playTrack(track);
    }
  };

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to purchase');
      navigate('/login');
      return;
    }

    if (userLicenses.includes(selectedLicense)) {
      toast.info('You already own this license');
      return;
    }

    setPurchasing(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/checkout/create`,
        {
          track_id: trackId,
          license_type: selectedLicense,
          origin_url: window.location.origin
        },
        
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      const message = error.response?.data?.detail || 'Failed to create checkout';
      toast.error(message);
    } finally {
      setPurchasing(false);
    }
  };

  const handleExclusiveInquiry = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to inquire about exclusive rights');
      navigate('/login');
      return;
    }
    if (track?.exclusive_sold) {
      toast.info('Exclusive rights for this track have already been sold');
      return;
    }
    if (user?.id === track?.artist_id) {
      toast.info("You can't open an exclusive inquiry on your own track");
      return;
    }
    try {
      const res = await axios.post(`${BACKEND_URL}/api/conversations`, {
        track_id: trackId,
        initial_message: `Hi ${track.artist_name}, I'm interested in purchasing exclusive rights for "${track.title}". Can we discuss terms?`
      });
      toast.success('Conversation opened — message the artist in your Inbox');
      navigate(`/inbox/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open conversation');
    }
  };

  const handleMessageArtist = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to message the artist');
      navigate('/login');
      return;
    }
    if (user?.id === track?.artist_id) {
      toast.info("That's you — can't message yourself");
      return;
    }
    try {
      const res = await axios.post(`${BACKEND_URL}/api/conversations`, {
        track_id: trackId,
      });
      navigate(`/inbox/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open conversation');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-dark pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  if (!track) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-dark pt-24 pb-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-8"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column - Artwork & Play */}
          <div>
            <div className="relative aspect-square bg-slate-900 border border-white/10 rounded-xl overflow-hidden group">
              <img
                src={track.artwork_url}
                alt={track.title}
                className="w-full h-full object-cover"
              />
              
              {/* Play Overlay */}
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={handlePlay}
                  className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center hover:bg-emerald-400 transition-colors transform hover:scale-105"
                  data-testid="track-detail-play"
                >
                  {isTrackPlaying ? (
                    <Pause className="w-8 h-8 text-slate-900" />
                  ) : (
                    <Play className="w-8 h-8 text-slate-900 ml-1" />
                  )}
                </button>
              </div>

              {/* Tags */}
              <div className="absolute top-4 left-4 flex gap-2">
                {track.genre && (
                  <span className="px-3 py-1.5 bg-black/60 backdrop-blur-sm text-sm text-white rounded-full">
                    {track.genre}
                  </span>
                )}
                {track.bpm && (
                  <span className="px-3 py-1.5 bg-black/60 backdrop-blur-sm text-sm text-slate-400 rounded-full">
                    {track.bpm} BPM
                  </span>
                )}
              </div>
            </div>

            {/* Track Info */}
            <div className="mt-6 grid grid-cols-3 gap-4">
              <div className="bg-slate-900/50 border border-white/5 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-slate-400 mb-1">
                  <Tag className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-wide">Genre</span>
                </div>
                <p className="text-white font-medium">{track.genre}</p>
              </div>
              <div className="bg-slate-900/50 border border-white/5 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-slate-400 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-wide">BPM</span>
                </div>
                <p className="text-white font-medium">{track.bpm || 'N/A'}</p>
              </div>
              <div className="bg-slate-900/50 border border-white/5 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-slate-400 mb-1">
                  <ShoppingCart className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-wide">Sales</span>
                </div>
                <p className="text-white font-medium">{track.total_sales || 0}</p>
              </div>
            </div>
          </div>

          {/* Right Column - Details & License Options */}
          <div>
            <div className="mb-8">
              <h1 className="text-4xl font-medium tracking-tight text-white mb-2" data-testid="track-detail-title">
                {track.title}
              </h1>
              <button
                onClick={() => navigate(`/artists/${track.artist_id}`)}
                className="flex items-center gap-2 text-slate-400 hover:text-emerald-400 transition-colors"
              >
                <User className="w-4 h-4" />
                {track.artist_name}
              </button>
              
              {track.description && (
                <p className="text-slate-400 mt-4 leading-relaxed">
                  {track.description}
                </p>
              )}

              {/* Message Artist — visible to everyone, any role */}
              {user?.id !== track.artist_id && (
                <Button
                  onClick={handleMessageArtist}
                  variant="outline"
                  className="mt-5 border-white/15 text-white hover:bg-white/5 rounded-lg"
                  data-testid="message-artist-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Message Artist
                </Button>
              )}
            </div>

            {/* License Options */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-white mb-4">Choose Your License</h2>
              <div className="space-y-3">
                {licenses.map((license) => {
                  const price = track[license.priceKey];
                  const isOwned = userLicenses.includes(license.type);
                  const isSelected = selectedLicense === license.type;

                  return (
                    <button
                      key={license.type}
                      onClick={() => !isOwned && setSelectedLicense(license.type)}
                      disabled={isOwned}
                      className={`w-full p-4 border rounded-lg transition-all text-left ${
                        isOwned
                          ? 'bg-emerald-500/10 border-emerald-500/30 cursor-default'
                          : isSelected
                          ? 'bg-emerald-500/10 border-emerald-500/50'
                          : 'bg-slate-900/50 border-white/10 hover:border-white/20'
                      }`}
                      data-testid={`license-option-${license.type}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-white">{license.name}</h3>
                            {isOwned && (
                              <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full flex items-center gap-1">
                                <Check className="w-3 h-3" />
                                Owned
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-400 mt-1">{license.description}</p>
                          <div className="flex flex-wrap gap-2 mt-3">
                            {license.features.map((feature, i) => (
                              <span
                                key={i}
                                className="px-2 py-1 bg-white/5 text-xs text-slate-400 rounded"
                              >
                                {feature}
                              </span>
                            ))}
                          </div>
                          <a
                            href={`${BACKEND_URL}/api/license-agreements/active/${license.type}/preview`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 mt-3 text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
                            data-testid={`view-license-${license.type}`}
                          >
                            <FileText className="w-3.5 h-3.5" />
                            View License
                          </a>
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-2xl font-semibold text-emerald-400">
                            {formatPrice(price)}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Purchase Button */}
            <Button
              onClick={handlePurchase}
              disabled={purchasing || userLicenses.includes(selectedLicense)}
              className="w-full bg-emerald-500 text-slate-900 hover:bg-emerald-400 font-semibold py-6 text-lg rounded-lg disabled:opacity-50"
              data-testid="purchase-button"
            >
              {purchasing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : userLicenses.includes(selectedLicense) ? (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  License Owned
                </>
              ) : (
                <>
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Purchase {licenses.find(l => l.type === selectedLicense)?.name} - {formatPrice(track[licenses.find(l => l.type === selectedLicense)?.priceKey])}
                </>
              )}
            </Button>

            {!isAuthenticated && (
              <p className="text-center text-sm text-slate-500 mt-4">
                Please{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="text-emerald-400 hover:text-emerald-300"
                >
                  sign in
                </button>{' '}
                to purchase
              </p>
            )}

            {/* Exclusive Rights CTA */}
            <div className="mt-6 p-5 bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-xl" data-testid="exclusive-cta-card">
              <h3 className="text-lg font-semibold text-white mb-1">Want exclusive rights?</h3>
              <p className="text-sm text-slate-300 mb-3">
                Acquire <span className="text-emerald-400 font-medium">all rights</span> to this track. Negotiate the price directly with the artist via in-app chat — payment and a signed contract are issued through Ziki Store.
              </p>
              {track.exclusive_sold ? (
                <span className="inline-block px-3 py-1.5 bg-slate-800 text-slate-400 text-xs rounded-full">
                  Exclusive rights sold
                </span>
              ) : (
                <Button
                  onClick={handleExclusiveInquiry}
                  variant="outline"
                  className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 hover:text-white"
                  data-testid="exclusive-inquiry-btn"
                >
                  Inquire about Exclusive Rights
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
